// Member display-name resolution for console read surfaces (PRD-001S). The management read models
// now expose a human display label plus a short, non-PII reference hash (`*_ref`, the 8-char sha
// suffix). Supervisors should see the NAME as primary text, with the ref available on hover only —
// never the raw hash as the primary label.
//
// Resolution prefers the explicit display-name column, then the legacy `*_label` column (which the
// migration aliases to the display label for one release), then a safe placeholder. This keeps the
// console correct both before and after the migration deploys (same PR wave): pre-migration the
// label is still "Guard abc12345" and no ref tooltip shows; post-migration the name renders with
// the ref on hover.

export interface ResolvedGuardName {
  /** Primary, human-facing label. Never empty. */
  name: string;
  /** Short reference hash for hover only (8-char digest), or null when unavailable. */
  ref: string | null;
}

function clean(value: string | null | undefined): string | null {
  return typeof value === "string" && value.trim().length > 0 ? value.trim() : null;
}

/**
 * Resolve the display name + hover ref for a guard/member row.
 * @param displayName the explicit display-name column (e.g. guard_display_name / member_display_name)
 * @param label the legacy redacted label (guard_label / member_label / guard_name)
 * @param ref the short reference hash (guard_ref / member_ref) — hover only
 * @param fallback placeholder when neither a name nor a label is present
 */
export function resolveGuardName(input: {
  displayName?: string | null;
  label?: string | null;
  ref?: string | null;
  fallback?: string;
}): ResolvedGuardName {
  const name = clean(input.displayName) ?? clean(input.label) ?? input.fallback ?? "Guard";
  return { name, ref: clean(input.ref) };
}

/** Hover title for a ref, e.g. "Ref 0575131c". Undefined when there is no ref (so no empty tooltip). */
export function guardRefTitle(ref: string | null | undefined): string | undefined {
  const cleaned = clean(ref ?? null);
  return cleaned === null ? undefined : `Ref ${cleaned}`;
}
